import * as XLSX from "xlsx";
import { TABLE_MAX_COLS, TABLE_MAX_ROWS } from "@/cms/blocks/table";
import {
  DEFAULT_COLUMN_WIDTH,
  MIN_COLUMN_WIDTH,
  type TableCellModel,
} from "./types";
import {
  cloneTable,
  createCell,
  createColumnWidths,
  getCellDisplayValue,
  getColumnCount,
} from "./model";

function cellToXlsxValue(table: TableCellModel[][], cell: TableCellModel) {
  const raw = String(cell.text ?? "");
  const trimmed = raw.trim();

  if (trimmed.startsWith("=")) {
    return {
      f: trimmed.slice(1),
      v: getCellDisplayValue(table, cell),
    };
  }

  const number = Number(trimmed.replaceAll(",", ""));
  if (trimmed !== "" && Number.isFinite(number)) return number;

  return raw;
}

export async function exportTableToXlsx(
  table: TableCellModel[][],
  columnWidths: number[],
  filename = "table.xlsx",
) {
  const aoa = table.map((row) =>
    row.map((cell) => (cell.hidden ? null : cellToXlsxValue(table, cell))),
  );
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);

  worksheet["!cols"] = columnWidths.map((width) => ({ wpx: width }));
  worksheet["!merges"] = table.flatMap((row, rowIndex) =>
    row.flatMap((cell, colIndex) =>
      !cell.hidden && (cell.rowspan > 1 || cell.colspan > 1)
        ? [
            {
              s: { r: rowIndex, c: colIndex },
              e: {
                r: rowIndex + cell.rowspan - 1,
                c: colIndex + cell.colspan - 1,
              },
            },
          ]
        : [],
    ),
  );

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Table");
  XLSX.writeFile(workbook, filename);
}

function importColumnWidths(worksheet: XLSX.WorkSheet, columnCount: number) {
  const cols = worksheet["!cols"];
  if (!Array.isArray(cols) || !cols.length) {
    return createColumnWidths(columnCount);
  }

  return Array.from({ length: columnCount }, (_, index) => {
    const width = cols[index]?.wpx;
    return typeof width === "number" && Number.isFinite(width)
      ? Math.max(MIN_COLUMN_WIDTH, Math.round(width))
      : DEFAULT_COLUMN_WIDTH;
  });
}

function applyXlsxMergesToTable(
  table: TableCellModel[][],
  worksheet: XLSX.WorkSheet,
) {
  const next = cloneTable(table);
  const merges = Array.isArray(worksheet["!merges"]) ? worksheet["!merges"] : [];

  for (const merge of merges) {
    const fromRow = merge?.s?.r;
    const fromCol = merge?.s?.c;
    const toRow = merge?.e?.r;
    const toCol = merge?.e?.c;

    if (
      !Number.isInteger(fromRow) ||
      !Number.isInteger(fromCol) ||
      !Number.isInteger(toRow) ||
      !Number.isInteger(toCol)
    ) {
      continue;
    }

    const anchor = next[fromRow]?.[fromCol];
    if (!anchor) continue;

    anchor.rowspan = Math.max(1, toRow - fromRow + 1);
    anchor.colspan = Math.max(1, toCol - fromCol + 1);
    anchor.hidden = false;

    for (let row = fromRow; row <= toRow; row += 1) {
      for (let col = fromCol; col <= toCol; col += 1) {
        if (row === fromRow && col === fromCol) continue;
        const coveredCell = next[row]?.[col];
        if (coveredCell) coveredCell.hidden = true;
      }
    }
  }

  return next;
}

export async function importXlsxFileToTable(file: File) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, {
    type: "array",
    cellDates: true,
    cellFormula: true,
    cellText: false,
  });
  const firstSheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[firstSheetName];

  if (!worksheet) {
    throw new Error("This workbook does not contain a readable worksheet.");
  }

  const range = XLSX.utils.decode_range(worksheet["!ref"] ?? "A1:A1");
  const rowCount = Math.min(range.e.r - range.s.r + 1, TABLE_MAX_ROWS);
  const colCount = Math.min(range.e.c - range.s.c + 1, TABLE_MAX_COLS);
  const nextTable = Array.from({ length: rowCount }, (_, rowOffset) =>
    Array.from({ length: colCount }, (_, colOffset) => {
      const rowIndex = range.s.r + rowOffset;
      const colIndex = range.s.c + colOffset;
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
      const xlsxCell = worksheet[address];
      let text = "";

      if (xlsxCell?.f) {
        text = `=${xlsxCell.f}`;
      } else if (xlsxCell?.v !== undefined && xlsxCell?.v !== null) {
        text =
          xlsxCell.v instanceof Date
            ? xlsxCell.v.toISOString().slice(0, 10)
            : String(xlsxCell.v);
      }

      return createCell(
        rowOffset,
        colOffset,
        rowOffset === 0 ? "header" : "cell",
        text,
      );
    }),
  );

  const mergedTable = applyXlsxMergesToTable(nextTable, worksheet);
  return {
    table: mergedTable,
    columnWidths: importColumnWidths(worksheet, getColumnCount(mergedTable)),
  };
}
